import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { uuid } from '@/infra/util/uuid';
import type { BiomeId } from '@/domain/world/types';
import type { EnemyRuntime } from '@/domain/combat/types';
import type { MutationId } from '@/domain/mutation/types';
import {
  startRun,
  endRun,
  addKill,
  addPick,
  shouldOfferPick,
  decideOutcomeAtHpZero,
} from '@/domain/expedition/run';
import {
  ENEMIES_PER_RUN_INITIAL,
  PICK_AT_KILLS,
  RUN_XP_PER_KILL_MAP,
  FAINT_HAPPINESS_PENALTY,
  FAINT_HP_AFTER,
  ATTACK_HITBOX_W,
  ATTACK_HITBOX_H,
  PLAYER_HIT_INVULNERABLE_MS,
} from '@/domain/expedition/types';
import { computeDamage } from '@/domain/combat/damage';
import { baseStatsFor } from '@/domain/combat/stats';
import { applyMutations } from '@/domain/mutation/apply';
import { rollPicks } from '@/domain/mutation/pick';
import { MUTATION_CATALOG } from '@/domain/mutation/catalog';
import { enemyDefById } from '@/domain/combat/enemy-defs';
import { composeEnemyPopulation } from '@/domain/combat/enemy-composition';
import { placeEnemySpecs } from '@/domain/combat/enemy-placement';
import { regionRegistry } from '@/domain/world/region-registry';
import { getWorldService } from './world';
import { getWorldEvolutionService } from './world-evolution';
import { saveCurrentSnapshot } from './save';
import {
  beginDodge,
  canAttack,
  canDodge,
  canUseSkill,
  isInvulnerable,
} from '@/domain/combat/action-timing';
import { advanceEnemyBehavior } from '@/domain/combat/enemy-behavior';
import { abilityForType, resolveAbility } from '@/domain/combat/abilities';
import {
  advanceProjectile,
  projectileHitsCircle,
  spawnProjectile,
} from '@/domain/combat/projectile';

let nextRid = 1;
let nextProjectileId = 1;
let lastEnemyTickAt = 0;

function normalizedDirection(dirX: number, dirY: number): { x: number; y: number } {
  const magnitude = Math.hypot(dirX, dirY);
  if (magnitude < 0.01) return { x: 1, y: 0 };
  return { x: dirX / magnitude, y: dirY / magnitude };
}

export interface CombatRunApi {
  start: (biome: BiomeId) => void;
  attack: (dirX: number, dirY: number, nowMs?: number) => boolean;
  dodge: (dirX: number, dirY: number, nowMs?: number) => boolean;
  useSkill: (dirX?: number, dirY?: number, nowMs?: number) => boolean;
  hitEnemy: (rid: number, dmg: number, crit: boolean) => void;
  damagePlayer: (amount: number, nowMs?: number) => boolean;
  extract: () => void;
  applyPick: (id: MutationId) => void;
  cancelPick: () => void;
  tickEnemies: (nowMs: number) => void;
  spawnEnemiesForBiome: (biome: BiomeId, runId: string) => void;
  isInRun: () => boolean;
}

let api: CombatRunApi | null = null;

function start(biome: BiomeId): void {
  const s = useStore.getState();
  const c = s.creature;
  if (!c || c.state.dead || c.lifecycle.stage === 'egg') return;
  const runId = uuid();
  const run = startRun({ id: runId, biome, nowMs: Date.now() });

  const baseStats = baseStatsFor(c);
  const permIds = (c.mutations ?? []).map((m) => m.id as MutationId);
  const stats = applyMutations(baseStats, permIds);
  s.resetCombatRuntime();
  s.setActiveRun(run);
  s.setInExpedition(true);
  s.setPlayerMaxHp(stats.maxHp);
  s.setPlayerHp(stats.maxHp);
  spawnEnemiesForBiome(biome, runId);
  eventBus.emit('combat.started', { biome, runId });
  logger.info('combat.start', { biome, runId });
}

function spawnEnemiesForBiome(biome: BiomeId, runId: string): void {
  const region = regionRegistry.get(biome);
  if (!region) {
    useStore.getState().setEnemies([]);
    return;
  }
  const enemies: EnemyRuntime[] = [];
  const specs = composeEnemyPopulation({ biome, runId, count: ENEMIES_PER_RUN_INITIAL });
  let placements = placeEnemySpecs(region, specs);
  for (let layout = 1; placements.length < specs.length && layout <= 24; layout += 1) {
    placements = placeEnemySpecs(
      region,
      specs.map((spec) => ({ ...spec, seed: `${spec.seed}:layout:${layout}` })),
    );
  }
  for (const { spec, point } of placements) {
    const def = spec.kind === 'fauna' ? enemyDefById(spec.defId) : null;
    if (spec.kind === 'fauna' && !def) continue;
    const position = {
      x: point.x,
      y: point.y,
      spawnX: point.x,
      spawnY: point.y,
    };
    if (spec.kind === 'fauna') {
      if (!def) continue;
      enemies.push({
        kind: 'fauna',
        rid: nextRid++,
        defId: def.id,
        type: def.type,
        archetype: def.archetype,
        hp: def.maxHp,
        maxHp: def.maxHp,
        damage: def.damage,
        speed: def.speed,
        attackCooldown: def.attackCooldown,
        lastAttackAt: 0,
        state: 'idle',
        behavior: { kind: 'idle' },
        rank: 'common',
        telegraphMs: def.telegraphMs,
        recoveryMs: def.recoveryMs,
        attackRange: def.attackRange,
        preferredRange: def.preferredRange,
        ...position,
        burnUntil: null,
        iceUntil: null,
      });
      continue;
    }
    const timing = timingForArchetype(spec.profile.archetype);
    enemies.push({
      kind: 'wild',
      rid: nextRid++,
      populationId: spec.populationId,
      genome: spec.genome,
      biome,
      scale: spec.profile.scale,
      patterns: spec.profile.patterns,
      type: spec.genome.primaryTypes[0]!,
      archetype: spec.profile.archetype,
      hp: spec.profile.maxHp,
      maxHp: spec.profile.maxHp,
      damage: spec.profile.damage,
      speed: spec.profile.speed,
      attackCooldown: spec.profile.attackCooldown,
      lastAttackAt: 0,
      state: 'idle',
      behavior: { kind: 'idle' },
      rank: spec.rank,
      ...timing,
      ...position,
      burnUntil: null,
      iceUntil: null,
    });
  }
  useStore.getState().setEnemies(enemies);
}

function attack(dirX: number, dirY: number, nowMs = Date.now()): boolean {
  const s = useStore.getState();
  if (!s.inExpedition || !canAttack(s.actionClock, nowMs)) return false;
  const c = s.creature;
  if (!c) return false;
  s.setActionClock({ ...s.actionClock, lastAttackAt: nowMs });
  const stats = currentStats();
  // Normalize dir
  const direction = normalizedDirection(dirX, dirY);
  const { x: dx, y: dy } = direction;

  // Hitbox: rect dimensionado, centrado a 24px do player na direção
  const px = s.playerX + dx * (ATTACK_HITBOX_W / 2 + 4);
  const py = s.playerY + dy * (ATTACK_HITBOX_W / 2 + 4);
  const halfW = ATTACK_HITBOX_W / 2;
  const halfH = ATTACK_HITBOX_H / 2;

  for (const e of s.enemies) {
    if (e.state === 'dead') continue;
    if (Math.abs(e.x - px) > halfW + 12 || Math.abs(e.y - py) > halfH + 12) continue;
    const dmg = computeDamage({
      baseDamage: stats.damage,
      attackerType: c.genome.primaryTypes[0]!,
      defenderType: e.type,
      critChance: stats.critChance,
    });
    hitEnemy(e.rid, dmg.damage, dmg.crit);
    if (stats.lifesteal) {
      const heal = Math.floor(dmg.damage * 0.2);
      useStore
        .getState()
        .setPlayerHp(
          Math.min(useStore.getState().playerMaxHp, useStore.getState().playerHp + heal),
        );
    }
    if (stats.burnTouch || stats.aether) {
      useStore.getState().updateEnemy(e.rid, { burnUntil: nowMs + 4000 });
    }
    if (stats.iceTouch || stats.aether) {
      useStore.getState().updateEnemy(e.rid, { iceUntil: nowMs + 2000 });
    }
    if (stats.doubleStrike && Math.random() < 0.25) {
      const dmg2 = computeDamage({
        baseDamage: stats.damage,
        attackerType: c.genome.primaryTypes[0]!,
        defenderType: e.type,
        critChance: stats.critChance,
      });
      hitEnemy(e.rid, dmg2.damage, dmg2.crit);
    }
  }
  eventBus.emit('combat.attack-used', { dirX: dx, dirY: dy, atMs: nowMs });
  return true;
}

function timingForArchetype(
  archetype: EnemyRuntime['archetype'],
): Pick<EnemyRuntime, 'telegraphMs' | 'recoveryMs' | 'attackRange' | 'preferredRange'> {
  if (archetype === 'fast')
    return { telegraphMs: 260, recoveryMs: 320, attackRange: 34, preferredRange: 0 };
  if (archetype === 'tank')
    return { telegraphMs: 650, recoveryMs: 720, attackRange: 48, preferredRange: 0 };
  return { telegraphMs: 500, recoveryMs: 550, attackRange: 180, preferredRange: 110 };
}

function dodge(dirX: number, dirY: number, nowMs = Date.now()): boolean {
  const s = useStore.getState();
  if (!s.inExpedition || !canDodge(s.actionClock, nowMs)) return false;
  const { x, y } = normalizedDirection(dirX, dirY);
  s.setDodgeVector({ x, y });
  s.setActionClock(beginDodge(s.actionClock, nowMs));
  eventBus.emit('combat.dodge-used', { dirX: x, dirY: y, atMs: nowMs });
  return true;
}

function useSkill(dirX = 1, dirY = 0, nowMs = Date.now()): boolean {
  const s = useStore.getState();
  const c = s.creature;
  if (!s.inExpedition || !c) return false;
  const type = c.genome.primaryTypes[0];
  if (!type) return false;
  const stats = currentStats();
  const ability = abilityForType(type);
  const cooldownMs = stats.overload ? ability.cooldownMs * 0.5 : ability.cooldownMs;
  if (!canUseSkill(s.actionClock, nowMs, cooldownMs)) return false;
  const direction = normalizedDirection(dirX, dirY);

  const livingEnemies = s.enemies.filter((enemy) => enemy.state !== 'dead');
  const outcome = resolveAbility({
    ability,
    origin: { x: s.playerX, y: s.playerY },
    direction,
    targets: livingEnemies.map((enemy) => ({ id: enemy.rid, x: enemy.x, y: enemy.y })),
  });

  s.setActionClock({
    ...s.actionClock,
    lastSkillAt: nowMs,
    invulnerableUntil: Math.max(s.actionClock.invulnerableUntil, nowMs + outcome.shieldMs),
  });
  if (stats.overload) {
    s.setPlayerHp(Math.max(0, s.playerHp - 5));
  }
  let totalDamage = 0;
  for (const rid of outcome.targetIds) {
    const e = useStore.getState().enemies.find((enemy) => enemy.rid === rid);
    if (!e || e.state === 'dead') continue;
    const dmg = computeDamage({
      baseDamage: Math.round(stats.damage * ability.damageMultiplier),
      attackerType: type,
      defenderType: e.type,
      critChance: stats.critChance,
    });
    hitEnemy(e.rid, dmg.damage, dmg.crit);
    totalDamage += dmg.damage;
  }

  for (const status of outcome.statuses) {
    if (status.kind === 'burn') {
      useStore.getState().updateEnemy(status.targetId, { burnUntil: nowMs + status.durationMs });
    } else {
      useStore.getState().updateEnemy(status.targetId, { iceUntil: nowMs + status.durationMs });
    }
  }
  if (outcome.movement) {
    s.setPlayerPos(s.playerX + outcome.movement.x, s.playerY + outcome.movement.y);
  }
  if (outcome.healRatio > 0 && totalDamage > 0) {
    const hp = useStore.getState().playerHp;
    useStore
      .getState()
      .setPlayerHp(
        Math.min(s.playerMaxHp, hp + Math.max(1, Math.floor(totalDamage * outcome.healRatio))),
      );
  }

  eventBus.emit('combat.skill-used', {
    type,
    effectKind: ability.effect.kind,
    dirX: direction.x,
    dirY: direction.y,
    atMs: nowMs,
  });
  return true;
}

function hitEnemy(rid: number, dmg: number, crit: boolean): void {
  const s = useStore.getState();
  const e = s.enemies.find((x) => x.rid === rid);
  if (!e || e.state === 'dead') return;
  const newHp = e.hp - dmg;
  if (newHp <= 0) {
    s.updateEnemy(rid, { state: 'dead', behavior: { kind: 'dead' }, hp: 0 });
    onKill(e);
    eventBus.emit('combat.enemy-hit', { rid, damage: dmg, crit, killed: true });
  } else {
    s.updateEnemy(rid, { hp: newHp });
    eventBus.emit('combat.enemy-hit', { rid, damage: dmg, crit, killed: false });
  }
}

function onKill(e: EnemyRuntime): void {
  const s = useStore.getState();
  const run = s.activeRun;
  if (!run) return;
  const xp = RUN_XP_PER_KILL_MAP[e.archetype];
  const before = run.enemiesKilled;
  const newRun = addKill(run, xp);
  s.setActiveRun(newRun);
  // U6: registra kill no bestiary
  if (e.kind === 'fauna') getWorldEvolutionService().recordKill(e.defId);
  else getWorldEvolutionService().recordWildDiscovery(e);
  if (shouldOfferPick(before, newRun.enemiesKilled, PICK_AT_KILLS)) {
    offerPick();
  }
}

function offerPick(): void {
  const s = useStore.getState();
  const run = s.activeRun;
  if (!run) return;
  const picks = rollPicks(Math.random, run.pickedMutations);
  s.setPendingPicks(picks);
  eventBus.emit('combat.mutation-offered', { picks: picks.map((p) => p.id) });
}

function applyPick(id: MutationId): void {
  const s = useStore.getState();
  const run = s.activeRun;
  if (!run) return;
  const def = MUTATION_CATALOG[id];
  if (!def) return;
  const newRun = addPick(run, { id, pickedAtMs: Date.now() });
  s.setActiveRun(newRun);
  // Re-aplica stats em cima do que já está
  const c = s.creature;
  if (c) {
    const baseStats = baseStatsFor(c);
    const permIds = (c.mutations ?? []).map((m) => m.id as MutationId);
    const allIds = [...permIds, ...newRun.pickedMutations.map((p) => p.id as MutationId)];
    const stats = applyMutations(baseStats, allIds);
    // Ajusta maxHp e cura proporcional
    const ratio = s.playerHp / Math.max(1, s.playerMaxHp);
    s.setPlayerMaxHp(stats.maxHp);
    s.setPlayerHp(Math.round(stats.maxHp * ratio));
  }
  s.clearPendingPicks();
  eventBus.emit('combat.mutation-applied', { id });
}

function cancelPick(): void {
  // Em U5 não permite cancelar — força pick. Mas mantém método para futuro.
  useStore.getState().clearPendingPicks();
}

function damagePlayer(amount: number, nowMs = Date.now()): boolean {
  const s = useStore.getState();
  if (isInvulnerable(s.actionClock, nowMs)) return false;
  const stats = currentStats();
  let dmg = amount;
  // shield: se passou 5s sem hit, +20% absorção (simplificação: -20% dano)
  if (stats.shield) {
    dmg = Math.round(dmg * 0.8);
  }
  const newHp = Math.max(0, s.playerHp - dmg);
  s.setActionClock({
    ...s.actionClock,
    invulnerableUntil: nowMs + PLAYER_HIT_INVULNERABLE_MS,
  });
  s.setPlayerHp(newHp);
  eventBus.emit('combat.player-hit', { damage: dmg, hpLeft: newHp, crit: false });
  if (newHp <= 0) {
    handleDeath();
  }
  return true;
}

function handleDeath(): void {
  const s = useStore.getState();
  const c = s.creature;
  if (!c) return;
  const stats = currentStats();
  // phoenix: 1x ressurge
  if (stats.phoenix && !runtimePhoenixUsed()) {
    setRuntimePhoenixUsed();
    s.setPlayerHp(Math.round(s.playerMaxHp * 0.3));
    return;
  }
  const decision = decideOutcomeAtHpZero({
    sick: c.state.sick,
    stage:
      c.lifecycle.stage === 'baby' ||
      c.lifecycle.stage === 'teen' ||
      c.lifecycle.stage === 'adult' ||
      c.lifecycle.stage === 'elder'
        ? c.lifecycle.stage
        : 'adult',
  });
  endRunWith(decision);
}

let _phoenixUsed = false;
function runtimePhoenixUsed(): boolean {
  return _phoenixUsed;
}
function setRuntimePhoenixUsed(): void {
  _phoenixUsed = true;
}
function resetPhoenix(): void {
  _phoenixUsed = false;
}

function extract(): void {
  endRunWith('extracted');
}

function endRunWith(outcome: 'extracted' | 'fainted' | 'died'): void {
  const s = useStore.getState();
  const run = s.activeRun;
  if (!run) return;
  const ended = endRun(run, outcome, Date.now());
  s.setActiveRun(ended);
  s.incrementTotalRuns();

  const c = s.creature;
  if (c) {
    if (outcome === 'fainted') {
      const next = {
        ...c,
        state: {
          ...c.state,
          happiness: Math.max(0, c.state.happiness - FAINT_HAPPINESS_PENALTY),
        },
      };
      s.replaceCreature(next);
    }
    if (outcome === 'died') {
      const next = {
        ...c,
        state: { ...c.state, dead: true, causeOfDeath: 'expedition' as const },
      };
      s.replaceCreature(next);
    }
    if (outcome === 'extracted' || outcome === 'fainted') {
      // Persiste mutações: rare→permanent, epic→heritable (para now: também permanent)
      const mutations = c.mutations ?? [];
      const newMutations = [...mutations];
      for (const p of ended.pickedMutations) {
        const def = MUTATION_CATALOG[p.id as MutationId];
        if (!def) continue;
        if (def.persistence === 'permanent' || def.persistence === 'heritable') {
          newMutations.push({ id: p.id, pickedAtMs: p.pickedAtMs });
        }
      }
      const baseStats = baseStatsFor(c);
      const allIds = newMutations.map((m) => m.id as MutationId);
      const newStats = applyMutations(baseStats, allIds);
      s.replaceCreature({ ...s.creature!, mutations: newMutations, maxHp: newStats.maxHp });
    }
  }

  s.setInExpedition(false);
  s.resetCombatRuntime();
  resetPhoenix();
  eventBus.emit('combat.ended', {
    runId: ended.id,
    outcome,
    kills: ended.enemiesKilled,
    xp: ended.xpGained,
  });
  // Volta player ao estado pós-expedição
  if (outcome === 'fainted' && c) {
    s.replaceCreature({
      ...s.creature!,
      state: { ...s.creature!.state, vitality: Math.max(20, s.creature!.state.vitality) },
    });
    s.setPlayerHp(FAINT_HP_AFTER);
  }
  // Sai do mundo se ainda estiver lá: faz exit normal
  if (outcome === 'fainted' || outcome === 'died') {
    getWorldService().exitWorld(false);
  }
  void saveCurrentSnapshot();
}

function tickEnemiesWithBehavior(nowMs: number): void {
  const initial = useStore.getState();
  const dtMs = lastEnemyTickAt === 0 ? 16 : Math.min(100, Math.max(0, nowMs - lastEnemyTickAt));
  lastEnemyTickAt = nowMs;

  for (const enemy of initial.enemies) {
    if (enemy.behavior.kind === 'dead' || enemy.state === 'dead') continue;
    const dx = initial.playerX - enemy.x;
    const dy = initial.playerY - enemy.y;
    const distance = Math.hypot(dx, dy);
    const direction = distance < 0.01 ? { x: 1, y: 0 } : { x: dx / distance, y: dy / distance };
    const result = advanceEnemyBehavior({
      archetype: enemy.archetype,
      rank: enemy.rank,
      state: enemy.behavior,
      nowMs,
      distance,
      direction,
      hp: enemy.hp,
      maxHp: enemy.maxHp,
      telegraphMs: enemy.telegraphMs,
      recoveryMs: enemy.recoveryMs,
      attackRange: enemy.attackRange,
      preferredRange: enemy.preferredRange,
    });
    const patch: Partial<EnemyRuntime> = {
      behavior: result.state,
      state:
        result.state.kind === 'idle' ? 'idle' : result.state.kind === 'dead' ? 'dead' : 'chase',
    };

    if (result.intent.kind === 'move') {
      const slow = enemy.iceUntil && nowMs < enemy.iceUntil ? 0.7 : 1;
      const step = enemy.speed * result.intent.speedMultiplier * slow * (dtMs / 1_000);
      patch.x = enemy.x + result.intent.dx * step;
      patch.y = enemy.y + result.intent.dy * step;
    } else if (result.intent.kind === 'lunge') {
      patch.x = enemy.x + result.intent.dx * 32;
      patch.y = enemy.y + result.intent.dy * 32;
      if (distance <= enemy.attackRange + 24) damagePlayer(enemy.damage, nowMs);
    } else if (result.intent.kind === 'slam') {
      if (distance <= result.intent.radius + 18) damagePlayer(enemy.damage, nowMs);
    } else if (result.intent.kind === 'shoot') {
      const projectile = spawnProjectile({
        id: `enemy-${enemy.rid}-${nextProjectileId++}`,
        owner: 'enemy',
        x: enemy.x,
        y: enemy.y,
        dirX: result.intent.dx,
        dirY: result.intent.dy,
        speed: 160,
        radius: 5,
        damage: enemy.damage,
        type: enemy.type,
        nowMs,
        lifetimeMs: 2_000,
      });
      useStore.getState().setProjectiles([...useStore.getState().projectiles, projectile]);
    }

    if (
      enemy.burnUntil &&
      nowMs < enemy.burnUntil &&
      Math.floor(nowMs / 1_000) !== Math.floor((nowMs - dtMs) / 1_000)
    ) {
      patch.hp = Math.max(0, enemy.hp - 2);
    }
    useStore.getState().updateEnemy(enemy.rid, patch);
  }

  for (const projectile of useStore.getState().projectiles) {
    if (nowMs >= projectile.expiresAt) {
      useStore.getState().removeProjectile(projectile.id);
      continue;
    }
    const next = advanceProjectile(projectile, dtMs);
    if (
      next.owner === 'enemy' &&
      projectileHitsCircle(next, {
        x: useStore.getState().playerX,
        y: useStore.getState().playerY,
        radius: 18,
      })
    ) {
      damagePlayer(next.damage, nowMs);
      useStore.getState().removeProjectile(next.id);
      continue;
    }
    useStore.getState().updateProjectile(next.id, next);
  }
}

function tickEnemies(nowMs: number): void {
  tickEnemiesWithBehavior(nowMs);
}

function currentStats(): ReturnType<typeof baseStatsFor> {
  const s = useStore.getState();
  const c = s.creature;
  if (!c)
    return baseStatsFor({
      id: '',
      name: '',
      genome: {} as never,
      state: {} as never,
      lifecycle: {} as never,
    });
  const baseStats = baseStatsFor(c);
  const permIds = (c.mutations ?? []).map((m) => m.id as MutationId);
  const runIds = (s.activeRun?.pickedMutations ?? []).map((p) => p.id as MutationId);
  return applyMutations(baseStats, [...permIds, ...runIds]);
}

export function getCombatRunService(): CombatRunApi {
  if (api) return api;
  api = {
    start,
    attack,
    dodge,
    useSkill,
    hitEnemy,
    damagePlayer,
    extract,
    applyPick,
    cancelPick,
    tickEnemies,
    spawnEnemiesForBiome,
    isInRun: () => useStore.getState().inExpedition,
  };
  return api;
}

export function resetCombatRunServiceForTests(): void {
  api = null;
  nextRid = 1;
  nextProjectileId = 1;
  lastEnemyTickAt = 0;
  resetPhoenix();
}
